// image-trail
import { onMount, onDestroy, onView } from "@/modules/_";
import { Raf, Resize } from "@lib/subs";
import { prefersReducedMotion } from "@/utils/media";
import { damp, map, clamp } from "@/utils/math";
import gsap, { Draggable } from "@lib/gsap";

const SPAWN_DISTANCE_MIN = 60;
const SPAWN_DISTANCE_MAX = 120;
const HOLD_MS = 500;
const ENTRY_DURATION = 0.9;
const EXIT_DURATION = 0.8;
const ENTRY_EASE = "expo.out";
const EXIT_EASE = "expo.out";

const ROUND = "10px";
const FALLBACK_W = 148;
const FALLBACK_H = 186;
const ICON_FALLBACK_W = 40;
const ICON_FALLBACK_H = 40;
const ROTATION_MIN_DEG = 2;
const ROTATION_MAX_DEG = 10;
const SCALE_FALLOFF = 0.05;
const SCALE_MIN = 0.7;

const MAX_ACTIVE_MULTIPLIER = 2;
const ICON_PROBABILITY = 0.66;

const AVOID_PADDING = 40;
const AVOID_HALO = 80;
const AVOID_PUSH = 30;

const VELOCITY_REF_PX_MS = 1.5;
const VELOCITY_DURATION_MIN_MULT = 0.8;
const VELOCITY_SCALE_MIN = 0.95;

const IDLE_DELAY_MS = 1500;
const IDLE_BREATH = 0.03;

// Gaps longer than this treat the next move as a fresh start — prevents stale velocity carrying across mouseleave/blur.
const CALIBRATION_GAP_MS = 500;

const CASCADE_STEP_MS = 60;

const SHADOW_Y_BASE = 8;
const SHADOW_BLUR_BASE = 24;
const SHADOW_ALPHA_BASE = 0.08;

const BURST_COUNT = 6;
const BURST_RADIUS_MIN = 90;
const BURST_RADIUS_MAX = 190;
const BURST_HOLD_MS = 350;
const BURST_LOCKOUT_MS = 200;
const BURST_ARC_SPAN_RAD = Math.PI * 1.35;
const BURST_ARC_JITTER_RAD = 0.14;

const REDUCED_MOTION_DISABLE = false;
const REDUCED_MOTION_FADE = 0.25;

const OFFSCREEN_ROOT_MARGIN = "200px";

const PRELOAD_TIMEOUT_MS = 2000;

const SPIN_AUTO_DURATION = 50;

const AUTO_PERFORM_ENABLED = true;
const AUTO_PERFORM_TOUCH_ONLY = true;
const AUTO_PERFORM_FIRST_DELAY_MS = 600;
const AUTO_PERFORM_FIRST_DURATION_MS = 2000;
const AUTO_PERFORM_AMBIENT_INTERVAL_MS = 8000;
const AUTO_PERFORM_AMBIENT_DURATION_MS = 1200;

const POSITION_PRESETS: { x: [number, number]; y: [number, number] }[] = [
  { x: [-5, 25], y: [-5, 20] },
  { x: [35, 65], y: [-10, 15] },
  { x: [75, 105], y: [-5, 20] },
  { x: [-10, 15], y: [35, 65] },
  { x: [85, 110], y: [35, 65] },
  { x: [-5, 25], y: [80, 105] },
  { x: [35, 65], y: [85, 110] },
  { x: [75, 105], y: [80, 105] },
];

export default function (element: HTMLElement, _dataset: DOMStringMap) {
  let rm = prefersReducedMotion();
  if (rm && REDUCED_MOTION_DISABLE) return;

  const isTouch = window.matchMedia("(hover: none)").matches;
  const shouldAutoPerform =
    AUTO_PERFORM_ENABLED && (!AUTO_PERFORM_TOUCH_ONLY || isTouch);

  const items = Array.from(
    element.querySelectorAll<HTMLElement>('[image-trail="item"]'),
  );
  const sources = items
    .map((item) => item.querySelector<HTMLImageElement>('[image-trail="img"]'))
    .filter((img): img is HTMLImageElement => !!img)
    .map((img) => img.currentSrc || img.src);

  if (sources.length === 0) {
    console.warn("[image-trail] no [image-trail='item'] sources found");
    return;
  }

  const iconItems = Array.from(
    element.querySelectorAll<HTMLElement>('[image-trail="icon-item"]'),
  );
  const iconSources = iconItems
    .map((item) =>
      item.querySelector<HTMLImageElement>('[image-trail="icon-img"]'),
    )
    .filter((img): img is HTMLImageElement => !!img)
    .map((img) => img.currentSrc || img.src);

  const list = element.querySelector<HTMLElement>('[image-trail="list"]');
  let listFallbackApplied = false;
  let ariaApplied = false;
  if (list) {
    if (!list.hasAttribute("aria-hidden")) {
      list.setAttribute("aria-hidden", "true");
      ariaApplied = true;
    }
    const cs = getComputedStyle(list);
    const isVisible =
      cs.display !== "none" &&
      cs.visibility !== "hidden" &&
      parseFloat(cs.opacity) > 0;
    if (isVisible) {
      Object.assign(list.style, {
        position: "absolute",
        pointerEvents: "none",
        opacity: "0",
        width: "0",
        height: "0",
        overflow: "visible",
      });
      listFallbackApplied = true;
    }
  }

  const iconList = element.querySelector<HTMLElement>(
    '[image-trail="icon-list"]',
  );
  let iconListFallbackApplied = false;
  let iconAriaApplied = false;
  if (iconList) {
    if (!iconList.hasAttribute("aria-hidden")) {
      iconList.setAttribute("aria-hidden", "true");
      iconAriaApplied = true;
    }
    const cs = getComputedStyle(iconList);
    const isVisible =
      cs.display !== "none" &&
      cs.visibility !== "hidden" &&
      parseFloat(cs.opacity) > 0;
    if (isVisible) {
      Object.assign(iconList.style, {
        position: "absolute",
        pointerEvents: "none",
        opacity: "0",
        width: "0",
        height: "0",
        overflow: "visible",
      });
      iconListFallbackApplied = true;
    }
  }

  const spinEl = element.querySelector<HTMLElement>('[image-trail="spin"]');
  let spinTimeline: gsap.core.Timeline | null = null;
  let spinDraggable: Draggable | null = null;
  if (spinEl) {
    spinEl.style.transformOrigin = "50% 50%";
    spinEl.style.willChange = "transform";
    spinTimeline = gsap.timeline({ repeat: -1 }).fromTo(
      spinEl,
      { rotation: 0 },
      { rotation: 360, duration: SPIN_AUTO_DURATION, ease: "none" },
    );

    const setSpinProgress = function (this: Draggable) {
      if (!spinTimeline) return;
      spinTimeline.progress(gsap.utils.wrap(0, 360, this.rotation) / 360);
    };

    [spinDraggable] = Draggable.create(spinEl, {
      type: "rotation",
      inertia: true,
      onPressInit: () => spinTimeline?.pause(),
      onDrag: setSpinProgress,
      onThrowUpdate: setSpinProgress,
      onThrowComplete: () => {
        spinTimeline?.resume();
      },
    });
  }

  const preloadedSrcs = new Set<string>();
  const preloadedIconSrcs = new Set<string>();
  let preloadFallback = false;
  const preloadFallbackTimer = window.setTimeout(() => {
    preloadFallback = true;
  }, PRELOAD_TIMEOUT_MS);

  function preload(urls: string[], target: Set<string>) {
    urls.forEach((u) => {
      const probe = new Image();
      probe.decoding = "async";
      probe.src = u;
      const done = () => target.add(u);
      if (probe.decode) probe.decode().then(done).catch(done);
      else {
        probe.onload = done;
        probe.onerror = done;
      }
    });
  }
  preload(sources, preloadedSrcs);
  preload(iconSources, preloadedIconSrcs);

  function ready(src: string, isIcon = false): boolean {
    if (preloadFallback) return true;
    return (isIcon ? preloadedIconSrcs : preloadedSrcs).has(src);
  }

  const avoidElements = Array.from(
    element.querySelectorAll<HTMLElement>('[image-trail="avoid"]'),
  );
  // Spin element doubles as an avoid zone so trail images don't smother interaction with it.
  if (spinEl) avoidElements.push(spinEl);
  let avoidRects: DOMRect[] = [];

  let trailW = FALLBACK_W;
  let trailH = FALLBACK_H;
  let iconW = ICON_FALLBACK_W;
  let iconH = ICON_FALLBACK_H;

  function remeasure() {
    const r = items[0]?.getBoundingClientRect();
    if (r && r.width > 0) trailW = r.width;
    if (r && r.height > 0) trailH = r.height;
    const ir = iconItems[0]?.getBoundingClientRect();
    if (ir && ir.width > 0) iconW = ir.width;
    if (ir && ir.height > 0) iconH = ir.height;
    avoidRects = avoidElements.map((e) => e.getBoundingClientRect());
  }
  remeasure();
  const offResize = Resize.add(remeasure);

  const positionWasUnset =
    !element.style.position && getComputedStyle(element).position === "static";
  if (positionWasUnset) element.style.position = "relative";

  // NaN sentinel = needs calibration; next mousemove sets position/time without spawning.
  let lastX = NaN;
  let lastY = NaN;
  let lastMoveT = performance.now();
  let smoothVel = 0;
  let active = true;
  let lockoutUntil = 0;

  const live = new Set<HTMLElement>();
  const fresh = new Set<HTMLElement>();
  const exitTimers = new Map<HTMLElement, number>();
  let cap = sources.length * (rm || isTouch ? 1 : MAX_ACTIVE_MULTIPLIER);
  let nextSpawnDistance = SPAWN_DISTANCE_MIN;

  let idleTimer: number | null = null;
  let idleAnim: gsap.core.Tween | null = null;
  let idleTarget: HTMLElement | null = null;
  let idleTargetPriorScale = 1;

  let imgDeck: number[] = [];
  let iconDeck: number[] = [];
  let lastImgIdx = -1;
  let lastIconIdx = -1;

  function refillDeck(n: number, last: number): number[] {
    const d = Array.from({ length: n }, (_, i) => i);
    for (let i = d.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [d[i], d[j]] = [d[j], d[i]];
    }
    if (n > 1 && d[d.length - 1] === last) {
      [d[d.length - 1], d[d.length - 2]] = [d[d.length - 2], d[d.length - 1]];
    }
    return d;
  }

  const pool: HTMLElement[] = [];
  const POOL_HIGH_WATER = cap + 4;

  function createWrapper(): HTMLElement {
    const wrapper = document.createElement("div");
    wrapper.className = "about_hero_trail_cell";
    wrapper.style.position = "absolute";
    wrapper.style.pointerEvents = "none";
    wrapper.style.translate = "-50% -50%";
    wrapper.style.filter =
      "drop-shadow(0 var(--td-sy, 8px) var(--td-sb, 24px) rgba(0,0,0, var(--td-sa, 0.08)))";
    const trailImg = document.createElement("img");
    trailImg.alt = "";
    trailImg.className = "about_hero_trail_img";
    trailImg.decoding = "async";
    trailImg.loading = "eager";
    trailImg.style.display = "block";
    trailImg.style.width = "100%";
    trailImg.style.height = "100%";
    trailImg.style.objectFit = "cover";
    wrapper.appendChild(trailImg);
    return wrapper;
  }

  function acquireWrapper(): HTMLElement {
    return pool.pop() ?? createWrapper();
  }

  function releaseWrapper(wrapper: HTMLElement) {
    if (wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
    const oldIcon = wrapper.querySelector(".about_hero_trail_icon");
    if (oldIcon && oldIcon.parentNode) oldIcon.parentNode.removeChild(oldIcon);
    wrapper.style.willChange = "auto";
    if (pool.length < POOL_HIGH_WATER) pool.push(wrapper);
  }

  function resolveAvoid(
    cx: number,
    cy: number,
  ): {
    suppress: boolean;
    ox: number;
    oy: number;
  } {
    let suppress = false;
    let ox = 0;
    let oy = 0;
    for (const r of avoidRects) {
      const nx = Math.max(r.left, Math.min(cx, r.right));
      const ny = Math.max(r.top, Math.min(cy, r.bottom));
      const ddx = cx - nx;
      const ddy = cy - ny;
      const dist = Math.hypot(ddx, ddy);
      if (dist < AVOID_PADDING) {
        suppress = true;
        continue;
      }
      if (dist < AVOID_HALO) {
        const cxr = (r.left + r.right) / 2;
        const cyr = (r.top + r.bottom) / 2;
        let ux = cx - cxr;
        let uy = cy - cyr;
        const ulen = Math.hypot(ux, uy) || 1;
        ux /= ulen;
        uy /= ulen;
        const push = map(dist, AVOID_PADDING, AVOID_HALO, AVOID_PUSH, 0);
        ox += ux * push;
        oy += uy * push;
      }
    }
    return { suppress, ox, oy };
  }

  function spawn(x: number, y: number, dx: number, opts?: { holdMs?: number }) {
    if (!imgDeck.length) imgDeck = refillDeck(sources.length, lastImgIdx);
    const idx = imgDeck.pop()!;
    lastImgIdx = idx;
    const src = sources[idx];
    if (!ready(src)) return;

    const vNorm = clamp(0, 1, smoothVel / VELOCITY_REF_PX_MS);
    const entryDur = rm
      ? REDUCED_MOTION_FADE
      : map(
          vNorm,
          0,
          1,
          ENTRY_DURATION,
          ENTRY_DURATION * VELOCITY_DURATION_MIN_MULT,
        );
    const startScale = rm ? 1 : map(vNorm, 0, 1, 1.2, VELOCITY_SCALE_MIN);
    const holdMs = opts?.holdMs ?? HOLD_MS;

    const sectionWidth = element.clientWidth || 1;
    const sign =
      Math.abs(dx) > 0.5 ? (dx > 0 ? 1 : -1) : x > sectionWidth / 2 ? 1 : -1;
    const rotMag =
      ROTATION_MIN_DEG + Math.random() * (ROTATION_MAX_DEG - ROTATION_MIN_DEG);
    const rotation = rm ? 0 : sign * rotMag;

    const wrapper = acquireWrapper();
    wrapper.style.left = `${x}px`;
    wrapper.style.top = `${y}px`;
    wrapper.style.width = `${trailW}px`;
    wrapper.style.height = `${trailH}px`;
    wrapper.style.willChange = "transform, clip-path, filter";
    wrapper.style.setProperty("--td-sa", String(SHADOW_ALPHA_BASE));
    wrapper.style.setProperty("--td-sy", `${SHADOW_Y_BASE}px`);
    wrapper.style.setProperty("--td-sb", `${SHADOW_BLUR_BASE}px`);

    const trailImg = wrapper.querySelector<HTMLImageElement>(
      ".about_hero_trail_img",
    )!;
    trailImg.src = src;

    let icon: HTMLImageElement | null = null;
    if (iconSources.length > 0 && Math.random() < ICON_PROBABILITY) {
      if (!iconDeck.length)
        iconDeck = refillDeck(iconSources.length, lastIconIdx);
      const iIdx = iconDeck.pop()!;
      lastIconIdx = iIdx;
      const iconSrc = iconSources[iIdx];
      if (ready(iconSrc, true)) {
        const preset =
          POSITION_PRESETS[Math.floor(Math.random() * POSITION_PRESETS.length)];
        const xPct = preset.x[0] + Math.random() * (preset.x[1] - preset.x[0]);
        const yPct = preset.y[0] + Math.random() * (preset.y[1] - preset.y[0]);
        icon = document.createElement("img");
        icon.src = iconSrc;
        icon.alt = "";
        icon.decoding = "async";
        icon.loading = "eager";
        icon.className = "about_hero_trail_icon";
        icon.style.position = "absolute";
        icon.style.left = `${xPct}%`;
        icon.style.top = `${yPct}%`;
        icon.style.width = `${iconW}px`;
        icon.style.height = `${iconH}px`;
        icon.style.objectFit = "contain";
        icon.style.pointerEvents = "none";
        wrapper.appendChild(icon);
      }
    }

    gsap.set(wrapper, { scale: startScale, rotation, opacity: 1 });
    gsap.set(trailImg, {
      clipPath: rm ? `inset(0% round ${ROUND})` : `inset(50% round 0px)`,
      opacity: rm ? 0 : 1,
    });
    if (icon) {
      const iconRotMag =
        ROTATION_MIN_DEG +
        Math.random() * (ROTATION_MAX_DEG - ROTATION_MIN_DEG);
      const iconRotation = rm ? 0 : (Math.random() < 0.5 ? -1 : 1) * iconRotMag;
      gsap.set(icon, {
        xPercent: -50,
        yPercent: -50,
        transformOrigin: "50% 50%",
        rotation: iconRotation,
        scale: rm ? 1 : 0,
        opacity: rm ? 0 : 1,
      });
    }

    element.appendChild(wrapper);
    live.add(wrapper);
    fresh.add(wrapper);

    if (rm) {
      gsap.to(trailImg, { opacity: 1, duration: entryDur });
      if (icon) gsap.to(icon, { opacity: 1, duration: entryDur });
    } else {
      gsap.to(wrapper, {
        scale: 1,
        duration: entryDur,
        ease: ENTRY_EASE,
        onComplete: () => {
          // Only clear will-change if the wrapper hasn't moved on to exit.
          if (fresh.has(wrapper)) wrapper.style.willChange = "auto";
        },
      });
      gsap.to(trailImg, {
        clipPath: `inset(0% round ${ROUND})`,
        duration: entryDur,
        ease: ENTRY_EASE,
      });
      if (icon) {
        gsap.to(icon, {
          scale: 1,
          duration: entryDur,
          ease: ENTRY_EASE,
        });
      }
    }

    const freshArr = Array.from(fresh);
    for (let i = 0; i < freshArr.length - 1; i++) {
      const older = freshArr[i];
      const rank = freshArr.length - 1 - i;
      const targetScale = Math.max(SCALE_MIN, 1 - rank * SCALE_FALLOFF);
      const shadowAlpha = clamp(0, 0.2, SHADOW_ALPHA_BASE - rank * 0.01);
      gsap.to(older, {
        scale: targetScale,
        "--td-sa": shadowAlpha,
        duration: rm ? REDUCED_MOTION_FADE : entryDur,
        ease: ENTRY_EASE,
        overwrite: "auto",
      } as gsap.TweenVars);
    }

    const t = window.setTimeout(() => exitImage(wrapper), holdMs);
    exitTimers.set(wrapper, t);

    while (fresh.size > cap) {
      const oldest = fresh.values().next().value;
      if (!oldest) break;
      exitImage(oldest);
    }
  }

  function exitImage(wrapper: HTMLElement) {
    if (!fresh.has(wrapper)) return;
    fresh.delete(wrapper);

    if (idleTarget === wrapper) {
      idleAnim?.kill();
      idleAnim = null;
      idleTarget = null;
    }

    const t = exitTimers.get(wrapper);
    if (t !== undefined) {
      clearTimeout(t);
      exitTimers.delete(wrapper);
    }

    const trailImg = wrapper.querySelector<HTMLImageElement>(
      ".about_hero_trail_img",
    );
    const icon = wrapper.querySelector<HTMLImageElement>(
      ".about_hero_trail_icon",
    );

    gsap.killTweensOf(wrapper);
    if (trailImg) gsap.killTweensOf(trailImg);
    if (icon) gsap.killTweensOf(icon);

    wrapper.style.willChange = "transform, clip-path, filter";
    const exitDur = rm ? REDUCED_MOTION_FADE : EXIT_DURATION;

    if (rm) {
      if (trailImg) gsap.to(trailImg, { opacity: 0, duration: exitDur });
      if (icon) gsap.to(icon, { opacity: 0, duration: exitDur });
      gsap.to(wrapper, {
        opacity: 0,
        duration: exitDur,
        onComplete: () => {
          live.delete(wrapper);
          releaseWrapper(wrapper);
        },
      });
    } else {
      if (trailImg) {
        gsap.to(trailImg, {
          clipPath: `inset(50% round 0px)`,
          duration: exitDur,
          ease: EXIT_EASE,
        });
      }
      if (icon) {
        gsap.to(icon, {
          scale: 0,
          duration: exitDur,
          ease: EXIT_EASE,
        });
      }
      gsap.to(wrapper, {
        scale: 1.2,
        duration: exitDur,
        ease: EXIT_EASE,
        onComplete: () => {
          live.delete(wrapper);
          releaseWrapper(wrapper);
        },
      });
    }
  }

  let autoPerformOff: (() => void) | null = null;
  let ambientTimer: number | null = null;
  let firstAutoPerformDone = false;

  function bezierPath(
    rect: DOMRect,
    t: number,
  ): { x: number; y: number } {
    const p0 = { x: 0.18, y: 0.32 };
    const p1 = { x: 0.40, y: 0.78 };
    const p2 = { x: 0.62, y: 0.22 };
    const p3 = { x: 0.84, y: 0.65 };
    const u = 1 - t;
    const fx =
      u * u * u * p0.x +
      3 * u * u * t * p1.x +
      3 * u * t * t * p2.x +
      t * t * t * p3.x;
    const fy =
      u * u * u * p0.y +
      3 * u * u * t * p1.y +
      3 * u * t * t * p2.y +
      t * t * t * p3.y;
    return {
      x: rect.left + rect.width * fx,
      y: rect.top + rect.height * fy,
    };
  }

  function sinePath(
    rect: DOMRect,
    t: number,
  ): { x: number; y: number } {
    const fx = t;
    const fy = 0.5 + 0.22 * Math.sin(t * Math.PI * 2);
    return {
      x: rect.left + rect.width * fx,
      y: rect.top + rect.height * fy,
    };
  }

  function runAutoPerform(
    durationMs: number,
    curve: (rect: DOMRect, t: number) => { x: number; y: number },
    onDone?: () => void,
  ) {
    if (!shouldAutoPerform || rm) return;
    cancelAutoPerform();
    const start = performance.now();
    const rect = element.getBoundingClientRect();
    autoPerformOff = Raf.add(() => {
      const elapsed = performance.now() - start;
      const t = clamp(0, 1, elapsed / durationMs);
      // ease-in-out for organic acceleration/deceleration
      const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      const { x, y } = curve(rect, eased);
      processMove(x, y);
      if (t >= 1) {
        cancelAutoPerform();
        onDone?.();
      }
    });
  }

  function cancelAutoPerform() {
    if (autoPerformOff) {
      autoPerformOff();
      autoPerformOff = null;
    }
  }

  function scheduleAmbient() {
    if (
      !shouldAutoPerform ||
      rm ||
      AUTO_PERFORM_AMBIENT_INTERVAL_MS <= 0 ||
      !active
    )
      return;
    cancelAmbient();
    ambientTimer = window.setTimeout(() => {
      ambientTimer = null;
      runAutoPerform(AUTO_PERFORM_AMBIENT_DURATION_MS, sinePath, scheduleAmbient);
    }, AUTO_PERFORM_AMBIENT_INTERVAL_MS);
  }

  function cancelAmbient() {
    if (ambientTimer !== null) {
      clearTimeout(ambientTimer);
      ambientTimer = null;
    }
  }

  function cascade() {
    Array.from(fresh).forEach((w, i) => {
      window.setTimeout(() => exitImage(w), i * CASCADE_STEP_MS);
    });
    lastX = NaN;
    lastY = NaN;
    smoothVel = 0;
  }
  function flushAll() {
    cancelAutoPerform();
    cancelAmbient();
    cascade();
  }

  function clearIdle() {
    if (idleTimer !== null) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }
    if (idleAnim) {
      idleAnim.kill();
      idleAnim = null;
    }
    if (idleTarget) {
      gsap.to(idleTarget, {
        scale: idleTargetPriorScale,
        duration: 0.3,
        overwrite: "auto",
      });
      idleTarget = null;
    }
  }

  function triggerIdle() {
    if (rm) return;
    const last = Array.from(fresh).at(-1) ?? null;
    if (!last) return;
    idleTarget = last;
    idleTargetPriorScale = (gsap.getProperty(last, "scale") as number) || 1;
    idleAnim = gsap.to(last, {
      scale: idleTargetPriorScale + IDLE_BREATH,
      duration: 0.9,
      yoyo: true,
      repeat: -1,
      ease: "sine.inOut",
    });
  }

  function burstAt(clientX: number, clientY: number) {
    if (rm || !active) return;
    const rect = element.getBoundingClientRect();
    const av = resolveAvoid(clientX, clientY);
    if (av.suppress) return;
    const bx = clientX - rect.left + av.ox;
    const by = clientY - rect.top + av.oy;
    const startAngle = -Math.PI / 2 - BURST_ARC_SPAN_RAD / 2;
    for (let i = 0; i < BURST_COUNT; i++) {
      const t = BURST_COUNT > 1 ? i / (BURST_COUNT - 1) : 0.5;
      const angle =
        startAngle +
        t * BURST_ARC_SPAN_RAD +
        (Math.random() * 2 - 1) * BURST_ARC_JITTER_RAD;
      const baseR = map(t, 0, 1, BURST_RADIUS_MIN, BURST_RADIUS_MAX);
      const jitterR = (Math.random() * 2 - 1) * 12;
      const r = clamp(BURST_RADIUS_MIN, BURST_RADIUS_MAX, baseR + jitterR);
      const px = bx + Math.cos(angle) * r;
      const py = by + Math.sin(angle) * r;
      spawn(px, py, Math.cos(angle) * 5, { holdMs: BURST_HOLD_MS });
    }
    lockoutUntil = performance.now() + BURST_LOCKOUT_MS;
  }

  function onBurst(event: MouseEvent) {
    burstAt(event.clientX, event.clientY);
  }

  function onTouchBurst(event: TouchEvent) {
    const t = event.changedTouches[0];
    if (!t) return;
    burstAt(t.clientX, t.clientY);
  }

  function processMove(clientX: number, clientY: number) {
    if (!active) return;
    const now = performance.now();
    if (now < lockoutUntil) return;

    clearIdle();
    idleTimer = window.setTimeout(triggerIdle, IDLE_DELAY_MS);

    const rect = element.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const elapsedMs = now - lastMoveT;
    if (!Number.isFinite(lastX) || elapsedMs > CALIBRATION_GAP_MS) {
      lastX = x;
      lastY = y;
      lastMoveT = now;
      smoothVel = 0;
      nextSpawnDistance = rm ? SPAWN_DISTANCE_MIN * 2 : SPAWN_DISTANCE_MIN;
      return;
    }

    const ddx = x - lastX;
    const ddy = y - lastY;
    const moved = Math.hypot(ddx, ddy);

    const safeElapsed = Math.max(1, elapsedMs);
    const inst = moved / safeElapsed;
    const dtSec = safeElapsed / 1000;
    if (Number.isFinite(inst)) {
      smoothVel = damp(smoothVel, inst, 8, dtSec);
      smoothVel = clamp(0, VELOCITY_REF_PX_MS * 1.5, smoothVel);
    }
    lastMoveT = now;

    if (moved < nextSpawnDistance) return;

    lastX = x;
    lastY = y;

    const vNorm = clamp(0, 1, smoothVel / VELOCITY_REF_PX_MS);
    const distMin = rm ? SPAWN_DISTANCE_MIN * 2 : SPAWN_DISTANCE_MIN;
    const distMax = rm ? SPAWN_DISTANCE_MAX * 2 : SPAWN_DISTANCE_MAX;
    nextSpawnDistance = map(vNorm, 0, 1, distMin, distMax);

    const av = resolveAvoid(clientX, clientY);
    if (av.suppress) return;

    spawn(x + av.ox, y + av.oy, ddx);
  }

  function onMouseMove(event: MouseEvent) {
    cancelAutoPerform();
    cancelAmbient();
    processMove(event.clientX, event.clientY);
  }

  function onTouchMove(event: TouchEvent) {
    cancelAutoPerform();
    cancelAmbient();
    const t = event.touches[0];
    if (t) processMove(t.clientX, t.clientY);
  }

  const rmMql = window.matchMedia("(prefers-reduced-motion: reduce)");
  function onRMChange(e: MediaQueryListEvent) {
    rm = e.matches;
    cap = sources.length * (rm || isTouch ? 1 : MAX_ACTIVE_MULTIPLIER);
    if (rm) flushAll();
  }
  rmMql.addEventListener("change", onRMChange);

  onMount(() => {
    element.addEventListener("mousemove", onMouseMove);
    element.addEventListener("touchmove", onTouchMove, { passive: true });
    element.addEventListener("click", onBurst);
    element.addEventListener("touchend", onTouchBurst, { passive: true });
    element.addEventListener("mouseleave", cascade);
    window.addEventListener("blur", cascade);

    onView(element, {
      rootMargin: OFFSCREEN_ROOT_MARGIN,
      autoStart: true,
      callback: ({ isIn }) => {
        active = isIn;
        if (!isIn) {
          flushAll();
          return;
        }
        if (shouldAutoPerform && !rm && !firstAutoPerformDone) {
          firstAutoPerformDone = true;
          window.setTimeout(() => {
            if (!active) return;
            runAutoPerform(
              AUTO_PERFORM_FIRST_DURATION_MS,
              bezierPath,
              scheduleAmbient,
            );
          }, AUTO_PERFORM_FIRST_DELAY_MS);
        }
      },
    });
  });

  onDestroy(() => {
    element.removeEventListener("mousemove", onMouseMove);
    element.removeEventListener("touchmove", onTouchMove);
    element.removeEventListener("click", onBurst);
    element.removeEventListener("touchend", onTouchBurst);
    element.removeEventListener("mouseleave", cascade);
    window.removeEventListener("blur", cascade);
    rmMql.removeEventListener("change", onRMChange);
    offResize();
    cancelAutoPerform();
    cancelAmbient();

    if (spinDraggable) spinDraggable.kill();
    if (spinTimeline) spinTimeline.kill();
    if (spinEl) {
      gsap.set(spinEl, { clearProps: "transform,rotation" });
      spinEl.style.transformOrigin = "";
      spinEl.style.willChange = "";
    }

    clearTimeout(preloadFallbackTimer);
    clearIdle();

    exitTimers.forEach((t) => clearTimeout(t));
    exitTimers.clear();

    live.forEach((wrapper) => {
      gsap.killTweensOf(wrapper);
      const trailImg = wrapper.querySelector(".about_hero_trail_img");
      const icon = wrapper.querySelector(".about_hero_trail_icon");
      if (trailImg) gsap.killTweensOf(trailImg);
      if (icon) gsap.killTweensOf(icon);
      wrapper.remove();
    });
    live.clear();
    fresh.clear();
    pool.length = 0;

    if (positionWasUnset) element.style.position = "";
    if (list && listFallbackApplied) {
      Object.assign(list.style, {
        position: "",
        pointerEvents: "",
        opacity: "",
        width: "",
        height: "",
        overflow: "",
      });
    }
    if (list && ariaApplied) list.removeAttribute("aria-hidden");
    if (iconList && iconListFallbackApplied) {
      Object.assign(iconList.style, {
        position: "",
        pointerEvents: "",
        opacity: "",
        width: "",
        height: "",
        overflow: "",
      });
    }
    if (iconList && iconAriaApplied) iconList.removeAttribute("aria-hidden");
  });
}
